<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { selectedSetting, authState, API_BASE_URL, company, getAuthToken, isDevUser} from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'
 


// users

const settingsSections = [
  {
    value: '/settings/profile',
    icon: 'mdi-account-outline',
    title: 'Person Profile',
    description: 'Manage your personal contact details.',
  },
  {
    value: '/settings/company',
    icon: 'mdi-account-outline',
    title: 'Company Profile',
    description: 'Manage your company contact details.',
  },

  {
    value: '/settings/team',
    icon: 'mdi-account-group-outline',
    title: 'Team',
    description: 'Invite teammates and manage account access.',
  },

  {
    value: '/settings/billing',
    icon: 'mdi-credit-card-outline',
    title: 'Billing',
    description: 'Payment methods, invoices, and billing email.',
  },
]

// Show UI sections
const activeSection = computed(
  () =>
    settingsSections.find(
      (section) => section.value === selectedSetting.value
    ) || settingsSections[0]
)





const saving = ref(false)
const saveSuccess = ref(false)
const userProfile = reactive({
  first_name: '',
  last_name: '',
  email: authState.email || '',
  role: '',
})

watch(
  () => authState.email,
  (email) => {
    if (!userProfile.email) {
      userProfile.email = email || ''
    }
  },
  { immediate: true }
)

watch(
  () => authState.uid,
  (uid) => {
    if (uid) {
      loadUser()
    }
  },
  { immediate: true }
)

async function loadUser() {
  if (!authState.uid) {
    return
  }

  try {
    const token = await getAuthToken()
    const response = await fetch(
      `${API_BASE_URL}/users?firebase_uid=${encodeURIComponent(authState.uid)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to load user')
    }

    const user = data.users?.[0]

    if (!user) {
      return
    }

    userProfile.first_name = user.first_name || ''
    userProfile.last_name = user.last_name || ''
    userProfile.email = user.email || authState.email || ''
    userProfile.role = user.role || ''
  }
  catch (error) {
    console.error('loadUser error:', error)
  }
}

async function saveCompany() {

  const companyPayload = {
    owner_firebase_uid: authState.uid,

    company_name: company.company_name,
    industry: company.industry,
    sub_industry: company.sub_industry,

    company_email: company.company_email,
    company_phone: company.company_phone,

    main_contact_name: company.main_contact_name,
  }

  try {
    saving.value = true
    saveSuccess.value = false

    // save code
    const token = await getAuthToken()
    const response = await fetch(
      `${API_BASE_URL}/companies/upsert`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(companyPayload)
      }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to save company')
    }

    if (data.company) {
      Object.assign(company, data.company)
    }

    saveSuccess.value = true

    getAuthToken().then(token => logAction(token, {
      action: 'company.update',
      label: 'Updated company profile',
      company_id: company.company_id,
    })).catch(() => {})

    setTimeout(() => {
      saveSuccess.value = false
    }, 3000)
  }
  catch (error) {
    console.error('saveCompany error:', error)
    throw error
  }
  finally {
    saving.value = false
  }
}

async function saveUser() {
  const userPayload = {
    firebase_uid: authState.uid,
    company_id: company.company_id || null,
    email: userProfile.email || authState.email || null,
    first_name: userProfile.first_name || null,
    last_name: userProfile.last_name || null,
    role: userProfile.role || (
      company.owner_firebase_uid === authState.uid ? 'owner' : 'staff'
    ),
  }

  try {
    saving.value = true
    saveSuccess.value = false

    const token = await getAuthToken()
    const response = await fetch(
      `${API_BASE_URL}/users/upsert`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(userPayload)
      }
    )

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to save user')
    }

    saveSuccess.value = true

    getAuthToken().then(token => logAction(token, {
      action: 'user.update',
      label: 'Updated person profile',
      company_id: company.company_id,
    })).catch(() => {})

    setTimeout(() => {
      saveSuccess.value = false
    }, 3000)
  }
  catch (error) {
    console.error('saveUser error:', error)
    throw error
  }
  finally {
    saving.value = false
  }
}

 


/*
 
 

const profile = reactive({})
const company = reactive({})
const preferences = reactive({})
const billing = reactive({})
 
await saveProfile(profilePayload)
await saveCompany(companyPayload)
*/


/*--------------------------------------------------
| Company
--------------------------------------------------*/
const industries = [
  'Trades',
  'Home Services',
  'Legal',
  'Healthcare',
  'Accounting & Tax',
  'Real Estate',
  'Food & Beverage',
  'Beauty & Wellness',
  'Marketing & Creative',
  'IT & Technology',
  'Consulting',
  'Education & Training',
  'Non-Profit',
  'Other',
]
const subIndustries = {
  'Trades': [
    'Plumbing',
    'Electrical',
    'HVAC',
    'Roofing',
    'Painting',
    'Flooring',
    'Concrete',
    'General Contractor',
    'Landscaping',
    'Other',
  ],

  'Home Services': [
    'Cleaning',
    'Junk Removal',
    'Pest Control',
    'Window Cleaning',
    'Pressure Washing',
    'Pool Service',
    'Handyman',
    'Other',
  ],

  'Legal': [
    'Family Law',
    'Criminal Law',
    'Personal Injury',
    'Corporate Law',
    'Real Estate Law',
    'Immigration Law',
    'Other',
  ],

  'Healthcare': [
    'Dental Clinic',
    'Medical Clinic',
    'Physiotherapy',
    'Chiropractic',
    'Psychology',
    'Massage Therapy',
    'Other',
  ],

  'Accounting & Tax': [
    'Bookkeeping',
    'Tax Preparation',
    'CPA Firm',
    'Payroll Services',
    'Business Accounting',
    'Other',
  ],

  'Real Estate': [
    'Realtor',
    'Property Management',
    'Mortgage Broker',
    'Commercial Real Estate',
    'Other',
  ],

  'Food & Beverage': [
    'Restaurant',
    'Cafe',
    'Bakery',
    'Food Truck',
    'Catering',
    'Bar / Pub',
    'Other',
  ],

  'Beauty & Wellness': [
    'Hair Salon',
    'Barber Shop',
    'Spa',
    'Esthetics',
    'Massage',
    'Nail Salon',
    'Other',
  ],

  'Marketing & Creative': [
    'Marketing Agency',
    'Graphic Design',
    'Video Production',
    'Photography',
    'Web Design',
    'Other',
  ],

  'IT & Technology': [
    'MSP',
    'Software Development',
    'Cybersecurity',
    'IT Consulting',
    'Web Development',
    'Other',
  ],

  'Consulting': [
    'Business Consulting',
    'Operations Consulting',
    'HR Consulting',
    'Management Consulting',
    'Other',
  ],

  'Education & Training': [
    'Tutoring',
    'Coaching',
    'Training Provider',
    'Online Courses',
    'Other',
  ],

  'Non-Profit': [
    'Charity',
    'Association',
    'Community Organization',
    'Foundation',
    'Other',
  ],

  'Other': [
    'Other',
  ],
}
</script>




































































<template>
  <main class="settings-page">
    <section class="settings-shell">
 

      <section class="settings-panel">
        <div class="panel-title">
          <span class="panel-icon">
            <v-icon size="22">{{ activeSection.icon }}</v-icon>
          </span>
          <div>
            <h2>{{ activeSection.title }}</h2>
            <p>{{ activeSection.description }}</p>
          </div>
        </div>

        <!------------------------------------------
        | Person Profile
        ------------------------------------------->
        <div
          v-if="activeSection.value === '/settings/profile'"
          class="form-grid"
        >
          <label class="field">
            <span>First Name</span>
            <input type="text" v-model="userProfile.first_name" />
          </label>

          <label class="field">
            <span>Last Name</span>
            <input type="text" v-model="userProfile.last_name" />
          </label>
       
         
          <label class="field">
            <span>Account Email</span>
            <input type="email" v-model="userProfile.email"  />
          </label>

           <!--
          <label class="field">
            <span>Notification Email</span>
            <input type="email" value=""  />
          </label>

          <label class="field">
            <span>Phone</span>
            <input type="tel" value="" />
          </label>-->


          <div class="form-actions">
            <button
              class="save-button"
              type="button"
              @click="saveUser"
              :disabled="saving"
            >
              <v-progress-circular
                v-if="saving"
                indeterminate
                size="18"
                width="2"
              />

              <template v-else-if="saveSuccess">
                <v-icon size="18">mdi-check-circle</v-icon>
                <span>Saved</span>
              </template>

              <template v-else>
                <v-icon size="18">mdi-content-save-outline</v-icon>
                <span>Save</span>
              </template>
            </button>
          </div>





        </div>



        <!------------------------------------------
        | Company Profile
        ------------------------------------------->
        <div
          v-if="activeSection.value === '/settings/company'"
          class="form-grid"
        >
          
          <label class="field">
            <span>Industry</span>
            <v-select
              class="form-select"
              v-model="company.industry"
              :items="industries || []"
              
              placeholder="Select industry"
              variant="outlined"
              density="comfortable"
              hide-details
            />
          </label>

          <label class="field">
            <span>Sub Industry</span>
            <v-select
              class="form-select"
              v-model="company.sub_industry"
              :items="subIndustries[company.industry] || []"
              placeholder="Select sub industry"
              variant="outlined"
              density="comfortable"
              hide-details
            />
          </label>



          <label class="field">
            <span>Company Name</span>
            <input class="form-field" type="text" v-model="company.company_name" />
          </label>

          <label class="field">
            <span>Main Contact Name</span>
            <input class="form-field" type="text" v-model="company.main_contact_name" />
          </label>

          <label class="field">
            <span>Company Email</span>
            <input  class="form-field" type="email" v-model="company.company_email"  />
          </label>

          <label class="field">
            <span>Company Phone</span>
            <input class="form-field" type="tel" v-model="company.company_phone"/>
          </label>

          <label class="field" v-if="company.company_id">
            <span>Company ID</span>
            <input class="form-field" type="tel" v-model="company.company_id" disabled style="color:grey"/>
          </label>
 
 

          <div class="form-actions">
            <button
              class="save-button"
              type="button"
              @click="saveCompany"
              :disabled="saving"
            >
              <v-progress-circular
                v-if="saving"
                indeterminate
                size="18"
                width="2"
              />

              <template v-else-if="saveSuccess">
                <v-icon size="18">mdi-check-circle</v-icon>
                <span>Saved</span>
              </template>

              <template v-else>
                <v-icon size="18">mdi-content-save-outline</v-icon>
                <span>Save</span>
              </template>
            </button>
          </div>
        </div>



        <!------------------------------------------
        | Billing
        ------------------------------------------->
        <div
          v-else-if="activeSection.value === '/settings/billing' && isDevUser"
          class="form-grid"
        >
          <label class="field">
            <span>Billing email</span>
            <input type="email" value="billing@tylandscaping.com" />
          </label>
          <label class="field">
            <span>Plan</span>
            <input type="text" value="Quotastic Pro" />
          </label>
          <label class="field">
            <span>Payment method</span>
            <input type="text" value="Visa ending 4242" />
          </label>
        </div>



        <!------------------------------------------
        | 
        ------------------------------------------->
        <div v-else-if="activeSection.value === '/settings/team' && isDevUser" class="form-grid">
          <label class="field">
            <span>Invite email</span>
            <input type="email" placeholder="name@example.com" />
          </label>
          <label class="field">
            <span>Role</span>
            <input type="text" value="Admin" />
          </label>
          <label class="field">
            <span>Status</span>
            <input type="text" value="1 active member" />
          </label>
        </div>
      </section>
    </section>
  </main>
</template>







<style scoped>
.settings-page {
  min-height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.settings-shell {
  width: min(980px, 100%);
  margin: 0 auto;
}

.settings-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.settings-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.settings-panel {
  padding: 16px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.06);
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-subtle);
}

.panel-icon {
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #1f5c4d;
  background: #eef8f5;
}

.panel-title h2 {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 850;
}

.panel-title p {
  margin: 4px 0 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
  margin-top: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 0;
}

.field span {
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.field input {
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.92rem;
  outline: none;
}

.form-select {
  width: 100%;
}

.form-select :deep(.v-field) {
  min-height: 44px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  box-shadow: none;
}

.form-select :deep(.v-field__outline),
.form-select :deep(.v-field__overlay) {
  display: none;
}

.form-select :deep(.v-field__input) {
  min-height: 42px;
  padding: 0 0 0 12px;
  color: var(--text-primary);
  font: inherit;
  font-size: 0.92rem;
}

.form-select :deep(.v-select__selection),
.form-select :deep(.v-select__selection-text) {
  margin: 0;
  color: var(--text-primary);
  font-size: 0.92rem;
}

.form-select :deep(.v-field__append-inner) {
  align-items: center;
  min-height: 42px;
  padding-top: 0;
}

.form-select :deep(.v-field--focused) {
  border-color: #9fc8ba;
  box-shadow: 0 0 0 3px rgba(31, 122, 99, 0.1);
}



.field input:focus {
  border-color: #9fc8ba;
  box-shadow: 0 0 0 3px rgba(31, 122, 99, 0.1);
}

.form-actions {
  display: flex;
  grid-column: 1 / -1;
  margin-top: 2px;
}

.save-button {
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;

  width: 250px;
}

.save-button:disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

@media (max-width: 700px) {
  .settings-page {
    padding: 20px 12px 94px;
  }

  .settings-header {
    align-items: stretch;
    flex-direction: column;
  }

  .settings-header h1 {
    font-size: 1.6rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .save-button {
    width: 100%;
  }
}
</style>
